import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, where, doc, updateDoc, arrayUnion, setDoc } from 'firebase/firestore';
import { db as firestore } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useAppContext, View } from '../../context/AppContext';
import { Group } from '../../types';
import { SkeletonPage } from '../ui/Skeleton';
import { UnifiedGroupCard } from './UnifiedGroupCard';
import { Modal } from '../ui/Modal';
import { createJoinRequest, notifyAdminsOfJoinRequest } from '../../utils/joinRequestHelpers';

interface JoinGroupGateProps {
    slugOrId: string;
}

export const JoinGroupGate: React.FC<JoinGroupGateProps> = ({ slugOrId }) => {
    const { t } = useTranslation();
    const { user, userData } = useAuth();
    const { showToast, goToPage } = useAppContext();
    
    const [group, setGroup] = useState<Group | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    
    // Joint states
    const [isJoining, setIsJoining] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [passwordInput, setPasswordInput] = useState('');

    useEffect(() => {
        const fetchGroup = async () => {
            if (!slugOrId) return;
            try {
                setLoading(true);
                const groupsRef = collection(firestore, 'groups');
                
                // Try finding by slug first (exact match, lowercase)
                const slugQuery = query(groupsRef, where('slug', '==', slugOrId.toLowerCase()));
                const slugSnapshot = await getDocs(slugQuery);
                
                if (!slugSnapshot.empty) {
                    setGroup({ id: slugSnapshot.docs[0].id, ...slugSnapshot.docs[0].data() } as Group);
                    return;
                }
                
                // If not found by slug, try by ID
                const idQuery = query(groupsRef, where('__name__', '==', slugOrId));
                const idSnapshot = await getDocs(idQuery);
                
                if (!idSnapshot.empty) {
                    setGroup({ id: idSnapshot.docs[0].id, ...idSnapshot.docs[0].data() } as Group);
                } else {
                    setError(true);
                }
            } catch (err) {
                console.error("Error fetching group:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchGroup();
    }, [slugOrId]);

    const handleJoin = async () => {
        if (!user || !group) return;
        
        if (group.privacyMode === 'locked') {
            setPasswordInput('');
            setPasswordModalOpen(true);
            return;
        }
        
        if (group.privacyMode === 'request') {
            setIsJoining(true);
            try {
                const req = await createJoinRequest(
                    user.uid,
                    user.displayName || t('common.unknownPlayer', 'Joueur Inconnu'),
                    user.photoURL || undefined,
                    group.id,
                    group.name
                );

                if (group.admins || group.ownerId) {
                    await notifyAdminsOfJoinRequest(req, group.admins || [], group.ownerId);
                }
                showToast(t('groups.requestSentToast', 'Demande envoyée'), 'success');
            } catch (err) {
                console.error("Error sending join request:", err);
                showToast(t('groups.requestError', 'Erreur lors de la demande'), 'error');
            } finally {
                setIsJoining(false);
            }
            return;
        }
        
        if (group.privacyMode === 'invite') {
            showToast(t('groups.inviteOnly', "Ce groupe est sur invitation uniquement."), "error");
            return;
        }

        // Open public group
        setIsJoining(true);
        try {
            const groupRef = doc(firestore, 'groups', group.id);
            await updateDoc(groupRef, {
                members: arrayUnion(user.uid)
            });

            // Make sure the player document is fully initialized
            const playerRef = doc(firestore, 'groups', group.id, 'players', user.uid);
            await setDoc(playerRef, {
                id: user.uid,
                name: userData?.displayName || userData?.pseudo || user.displayName || t('common.unknownPlayer', 'Joueur Inconnu'),
                pseudo: userData?.pseudo || user.displayName || t('common.unknownPlayer', 'Joueur Inconnu'),
                displayName: userData?.displayName || user.displayName || t('common.unknownPlayer', 'Joueur Inconnu'),
                email: user.email || '',
                photoURL: userData?.photoURL || user.photoURL || '',
                avatar: userData?.photoURL || user.photoURL || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default',
                role: 'player',
                groupRole: 'player',
                level: userData?.level || t('groups.level1', '1. Niveau débutant'),
                hand: userData?.hand || t('profile.handRight', 'Droitier'),
                rating: userData?.rating || 1500,
                rd: userData?.rd || 350,
                volatility: userData?.volatility || 0.06,
                matchesPlayed: 0,
                wins: 0,
                losses: 0,
                winRate: 0,
                badges: [],
                ratingHistory: [],
                groupJoinDate: new Date().toISOString(),
                joinedAt: new Date().toISOString()
            });

            showToast(t('groups.welcomeTo', 'Bienvenue dans {{name}}', { name: group.name }), 'success');
            // Reload the page to refresh membership state in GroupWrapper
            // Staying on current URL allows landing back on the tournament/match if that was the entry point
            window.location.reload(); 
        } catch (error) {
            console.error("Error joining group:", error);
            showToast(t('groups.joinError', 'Erreur lors de la tentative'), 'error');
        } finally {
            setIsJoining(false);
        }
    };

    const handleUnlockGroup = async () => {
        if (!group) return;
        if (passwordInput === group.password) {
            // Unlock success, now join
            setPasswordModalOpen(false);
            
            // Re-run the join logic forcing privacy bypass
            const originalPrivacy = group.privacyMode;
            group.privacyMode = 'open'; // temporarily set local state to bypass check
            await handleJoin();
            group.privacyMode = originalPrivacy; // restore
        } else {
            showToast(t('groups.incorrectPassword', 'Mot de passe incorrect'), 'error');
        }
    };

    if (loading) {
        return <SkeletonPage />;
    }

    if (error || !group) {
        return (
            <div className="flex flex-col items-center justify-center p-8 space-y-4 text-center mt-20">
                <span className="material-symbols-outlined text-6xl text-text-secondary opacity-50">search_off</span>
                <h2 className="text-xl font-bold text-text-primary">Groupe Introuvable</h2>
                <p className="text-text-secondary">Le lien est invalide ou le groupe a été supprimé.</p>
                <button 
                    onClick={() => goToPage(View.DASHBOARD)} 
                    className="mt-4 px-6 py-2 bg-primary text-white font-bold rounded-xl"
                >
                    Retour à l'accueil
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto p-4 flex flex-col items-center justify-center min-h-[70vh] space-y-8 animate-fadeIn">
            
            <div className="text-center space-y-2">
                <h1 className="text-2xl font-bold text-primary">Rejoindre un Groupe</h1>
                <p className="text-text-secondary">Vous avez été invité à rejoindre :</p>
            </div>

            <div className="w-full pointer-events-none">
                <UnifiedGroupCard 
                    group={group}
                    user={user}
                    goToPage={goToPage}
                />
            </div>

            <button 
                onClick={handleJoin}
                disabled={isJoining || group.privacyMode === 'invite'}
                className="w-full py-4 bg-primary hover:bg-primary-hover text-white font-black text-lg rounded-2xl shadow-lg flex justify-center items-center gap-2 transform active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isJoining ? (
                    <span className="material-symbols-outlined animate-spin text-2xl">autorenew</span>
                ) : (
                    <>
                        {group.privacyMode === 'locked' && <span className="material-symbols-outlined text-xl">lock</span>}
                        {group.privacyMode === 'request' && <span className="material-symbols-outlined text-xl">back_hand</span>}
                        {group.privacyMode === 'invite' && <span className="material-symbols-outlined text-xl">block</span>}
                        {(!group.privacyMode || group.privacyMode === 'open') && <span className="material-symbols-outlined text-xl">login</span>}
                        
                        <span>
                            {group.privacyMode === 'locked' ? 'Déverrouiller et Rejoindre' : 
                             group.privacyMode === 'request' ? 'Demander à rejoindre' : 
                             group.privacyMode === 'invite' ? 'Sur invitation uniquement' : 
                             'Rejoindre maintenant'}
                        </span>
                    </>
                )}
            </button>

            {/* Password Modal */}
            <Modal
                isOpen={passwordModalOpen}
                onClose={() => setPasswordModalOpen(false)}
                title={`${t('groups.accessCodeFor', "Code d'accès pour")} ${group.name}`}
            >
                <div className="space-y-4">
                    <p className="text-text-secondary">{t('groups.privateGroupMessage', "Ce groupe est privé. Veuillez entrer le code d'accès.")}</p>
                    <input
                        type="password"
                        value={passwordInput}
                        onChange={e => setPasswordInput(e.target.value)}
                        placeholder={t('groups.accessCodePlaceholder', "Code d'accès")}
                        className="w-full bg-surface-3 border border-glass-border rounded-xl p-3 text-text-primary focus:border-primary outline-none"
                        autoComplete="off"
                    />
                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setPasswordModalOpen(false)} className="px-4 py-2 text-text-secondary font-medium">{t('groups.cancel', 'Annuler')}</button>
                        <button onClick={handleUnlockGroup} className="px-4 py-2 bg-primary text-white rounded-xl font-bold">{t('groups.submit', 'Valider')}</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
